/**
 * Client mirror of the server's headless-admin API-key scopes.
 *
 * An `agent-tools:*-admin` scope on a key exposes a family of `adminOnly` Fias
 * AI tools to an external agent (MCP) — see `ADMIN_KEY_SCOPES` in
 * `services/api-service/src/services/api-key-scopes.ts`, which is the
 * authoritative definition. This file duplicates the scope strings and their
 * authorized roles because the client bundle cannot import `@fias/entities`
 * (server-only transitive deps) — the same mirroring pattern as
 * `integration-entity-permissions.ts` and `client-entity-registry.ts`.
 *
 * `client-agent-admin-scope-mirror.test.ts` pins this list against the server
 * registry in both directions, so a new admin scope cannot appear on one side
 * only.
 *
 * WHY THE UI OFFERS THESE AT ALL. It did not, and that was the defect. The
 * mint route has accepted these scopes since 2026-08-27, gated on the minter
 * currently holding an authorizing role, their roles being current, and the
 * privileged-MFA gate passing. But Account Settings only ever sent
 * `agent-tools:read` / `:write` / `workspace:`, so the only way to get an
 * admin-scoped key was to hand-roll an HTTP call — which meant an authorized
 * Lit Legion admin connected their MCP client, saw none of the Lit Legion
 * tools, and had no way to find out why. The server is the security boundary
 * here; this list only decides what the form shows.
 *
 * `label` / `description` are UI copy and have no server counterpart — the
 * mirror test checks the scope + roles, not the prose.
 */

export interface AgentAdminScope {
  /** The persisted key scope, exactly as the server's registry names it. */
  scope: string;
  /**
   * Roles that authorize minting it. Mirrors the server's `authorizedRoles`
   * verbatim — including `admin`, which is listed rather than special-cased
   * so a membership check here is the same expression the mint route runs.
   */
  authorizedRoles: readonly string[];
  label: string;
  description: string;
}

export const AGENT_ADMIN_SCOPES: readonly AgentAdminScope[] = [
  {
    scope: 'agent-tools:game-config-admin',
    authorizedRoles: ['admin', 'litcraft_admin'],
    label: 'Game config publishing',
    description:
      'Read and publish the shared game config documents — LitCraft menus, game lists, task and reward panels — that every game reads, not your own data. A write only replaces the version the agent read, so nobody’s newer edit is overwritten. Your role is re-checked on every call, and the key stops working if it is revoked.',
  },
  {
    scope: 'agent-tools:store-admin',
    authorizedRoles: ['admin'],
    label: 'Arche Store curation',
    description:
      'Curate the public Arche Store — featured apps, hidden apps, stories, collections and daily picks, Discover words, artwork and built-in screenshots — which everyone sees, not your own data. A change only replaces the version the agent read, so nobody’s newer edit is overwritten, and each one is recorded under your name with this key. Turning the store on or off, deleting a story and deciding held screenshots stay in the Dev Console. Your role is re-checked on every call, and the key stops working if it is revoked.',
  },
  {
    scope: 'agent-tools:lit-legion-admin',
    authorizedRoles: ['admin', 'litcraft_admin'],
    label: 'Lit Legion content authoring',
    description:
      'Read and edit Lit Legion tasks and NPCs — the shared game catalog every player sees, not your own data — and hand players the tasks they are missing after a quest changes. Your role is re-checked on every call, and the key stops working if it is revoked.',
  },
];

/**
 * The admin scopes these roles may mint.
 *
 * Mirrors the mint route's check (`ADMIN_KEY_SCOPES[scope].authorizedRoles
 * .some((r) => roles.includes(r))`) so the form offers exactly what the server
 * will accept: offering more produces a 403 the user cannot act on, offering
 * less is the gap this whole file closes.
 *
 * Role membership alone is not sufficient at mint — roles must also be current
 * and the privileged-MFA gate must pass. Those are server-side and surface as
 * an error on submit; neither is knowable here.
 */
export function agentAdminScopesForRoles(roles: readonly string[] | undefined): AgentAdminScope[] {
  if (!roles || roles.length === 0) return [];
  return AGENT_ADMIN_SCOPES.filter((entry) =>
    entry.authorizedRoles.some((role) => roles.includes(role)),
  );
}

/** The admin scopes carried by a key, for labelling it in the list. */
export function agentAdminScopesOf(scopes: readonly string[]): AgentAdminScope[] {
  return AGENT_ADMIN_SCOPES.filter((entry) => scopes.includes(entry.scope));
}
